/*
 * Store kit cart helper (ES module).
 *
 * Adds and removes cart lines the same way Horizon's own product form does: it announces a
 * CartLinesUpdateEvent, asks the cart endpoint to return the cart drawer HTML, then resolves
 * the event with it. Horizon's cart drawer, cart items and cart icon listen for that event, so
 * the drawer refreshes (and slides open on add) without a page load.
 *
 * Loaded with <script type="module">. Keep the static import below: Shopify's asset pipeline
 * rewrites dynamic import() in non-module files into require(), which breaks in the browser.
 *
 * Also wires the add-on switches rendered in the drawer by snippets/kit-cart-addons.liquid.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

function cartSectionIds() {
  const ids = [];
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el.dataset.sectionId && !ids.includes(el.dataset.sectionId)) ids.push(el.dataset.sectionId);
  });
  return ids;
}

async function postJSON(path, body) {
  const res = await fetch(root + path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  return { ok: res.ok && !data.status && !data.errors, data };
}

function errorMessage(data, fallback) {
  return (data && (data.description || data.message || data.errors)) || fallback;
}

async function add(items, source) {
  const ids = cartSectionIds();

  // No cart drawer on this page (for example the cart page itself): plain add, then go to the cart.
  if (!ids.length) {
    const res = await postJSON('cart/add.js', { items });
    if (!res.ok) throw new Error(errorMessage(res.data, 'Could not add to cart.'));
    window.location.href = root + 'cart';
    return res.data;
  }

  const deferred = CartLinesUpdateEvent.createPromise();
  (source || document).dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      promise: deferred.promise,
    })
  );

  try {
    const res = await postJSON('cart/add.js', { items, sections: ids.join(','), sections_url: window.location.pathname });
    const cart = await fetch(root + 'cart.js').then((r) => r.json());
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'store-kit',
        sourceId: 'store-kit',
        itemCount: items.reduce((n, item) => n + item.quantity, 0),
        sections: res.ok ? res.data.sections : undefined,
        didError: !res.ok,
      },
    });
    if (!res.ok) throw new Error(errorMessage(res.data, 'Could not add to cart.'));
    return cart;
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

async function change(lineKey, quantity, source) {
  const ids = cartSectionIds();
  const deferred = CartLinesUpdateEvent.createPromise();
  (source || document).dispatchEvent(
    new CartLinesUpdateEvent({
      action: quantity > 0 ? 'update' : 'remove',
      context: 'cart',
      lines: [{ id: lineKey, quantity }],
      promise: deferred.promise,
    })
  );

  const res = await postJSON('cart/change.js', { id: lineKey, quantity, sections: ids.join(','), sections_url: window.location.pathname });
  if (!res.ok) {
    const err = new Error(errorMessage(res.data, 'Could not update the cart.'));
    deferred.reject(err);
    throw err;
  }
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(res.data),
    detail: {
      items: res.data.items,
      itemCount: res.data.item_count,
      sections: res.data.sections,
      source: 'store-kit',
      didError: false,
    },
  });
  return res.data;
}

window.KitCart = { add, change };

// Add-on switches inside the cart drawer. They are re-rendered after every cart update,
// so listen on the document instead of binding to each element.
document.addEventListener('change', (event) => {
  const toggle = event.target;
  if (!(toggle instanceof HTMLInputElement) || !toggle.matches('[data-kit-addon-toggle]')) return;

  const row = toggle.closest('.kit-cart-addon');
  row?.classList.add('is-busy');
  toggle.disabled = true;

  const request = toggle.checked
    ? add([{ id: Number(toggle.dataset.variantId), quantity: 1 }], toggle)
    : change(toggle.dataset.lineKey, 0, toggle);

  request.catch(() => {
    toggle.checked = !toggle.checked;
    toggle.disabled = false;
    row?.classList.remove('is-busy');
  });
});
